import React, { useState, useEffect } from 'react';
import { Search, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { pharmacyPOSService } from '../../services/pharmacy-pos.service';
import type { Medicine } from '../../types/pharmacy-pos';
import toast from 'react-hot-toast';

interface MedicineTableProps {
  onAddToCart: (medicine: Medicine) => void;
  disabled?: boolean;
}

export const MedicineTable: React.FC<MedicineTableProps> = ({
  onAddToCart,
  disabled = false
}) => {
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadMedicines();
  }, [searchQuery, page, pageSize]);

  const loadMedicines = async () => {
    setLoading(true);
    try {
      const result = await pharmacyPOSService.searchMedicines(searchQuery, page, pageSize);
      setMedicines(result.items);
      setTotal(result.total);
    } catch (error) {
      console.error('Error loading medicines:', error);
      toast.error('Failed to load medicines');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const getStockBadgeClass = (stock: number): string => {
    if (stock <= 0) return 'bg-gray-100 text-gray-600';
    if (stock <= 5) return 'bg-red-100 text-red-600';
    if (stock <= 10) return 'bg-yellow-100 text-yellow-600';
    return 'bg-green-100 text-green-600';
  };

  const getExpiryBadgeClass = (expiryDate?: string): string => {
    if (!expiryDate) return 'text-gray-500';
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return 'text-red-600 font-semibold'; // Expired
    if (daysUntilExpiry <= 30) return 'text-red-600'; // ≤30 days
    if (daysUntilExpiry <= 90) return 'text-yellow-600'; // ≤90 days
    return 'text-gray-600'; // >90 days
  };

  const formatExpiryDate = (expiryDate?: string): string => {
    if (!expiryDate) return 'N/A';
    const date = new Date(expiryDate);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleKeyDown = (e: React.KeyboardEvent, medicine: Medicine) => {
    if (e.key === 'Enter') {
      handleAddToCart(medicine);
    }
  };

  const handleAddToCart = (medicine: Medicine) => {
    if (medicine.stock <= 0) {
      toast.error('Medicine out of stock');
      return;
    }
    onAddToCart(medicine);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
      {/* Header */}
      <div className="bg-green-600 text-white px-4 py-3 rounded-t-lg">
        <h2 className="text-lg font-semibold">Medicine Sell</h2>
      </div>

      {/* Controls */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setPage(1);
              }}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span className="text-sm text-gray-600">entries</span>
          </div>

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 border-b">SN</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 border-b">Medicine</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 border-b">Cate</th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 border-b">Price</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700 border-b">Quantity</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700 border-b">Expire</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700 border-b">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  Loading medicines...
                </td>
              </tr>
            ) : medicines.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No medicines found
                </td>
              </tr>
            ) : (
              medicines.map((medicine, index) => (
                <tr
                  key={medicine.id}
                  tabIndex={0}
                  onKeyDown={(e) => handleKeyDown(e, medicine)}
                  className="border-b border-gray-100 hover:bg-gray-50 focus:bg-blue-50 focus:outline-none cursor-pointer"
                >
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {(page - 1) * pageSize + index + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{medicine.name}</div>
                    {medicine.generic_name && (
                      <div className="text-xs text-gray-500">{medicine.generic_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {medicine.category || 'Tablet'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                    {medicine.mrp.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStockBadgeClass(medicine.stock)}`}>
                      {medicine.stock}
                      {medicine.stock <= 10 && medicine.stock > 0 && (
                        <span className="ml-1">↓</span>
                      )}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-center text-sm ${getExpiryBadgeClass(medicine.expiry_date)}`}>
                    {formatExpiryDate(medicine.expiry_date)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleAddToCart(medicine)}
                      disabled={disabled || medicine.stock <= 0}
                      className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span>Add</span>
                      <Plus className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
        <div className="text-sm text-gray-600">
          Showing {medicines.length === 0 ? 0 : (page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} entries
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-1 bg-blue-600 text-white rounded text-sm font-medium">
            {page}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
